/**
 * useFormDraft
 *
 * Auto-saves a react-hook-form's values to durable device storage (debounced)
 * and silently restores them when the form mounts again — so a user never loses
 * what they typed when the app is backgrounded, reopened, or killed by the OS.
 *
 * Drafts are scoped per user and per form key. Call the returned `clearDraft()`
 * after a successful submit or an explicit discard so the next open starts clean.
 *
 * Never pass passwords / sensitive fields through here — use `exclude` or simply
 * do not attach the hook to such forms.
 */
import { useEffect, useRef } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { useAuth } from '@/contexts/AuthContext';
import { clearDraft, loadDraft, saveDraft, scopedDraftKey } from '@/lib/formDraftStorage';

const DEFAULT_DEBOUNCE_MS = 600;
const DEFAULT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface UseFormDraftOptions {
  /** Stable form id, e.g. 'expense-entry' or `project-edit:${id}` for edit mode. */
  key: string;
  /** Field names to never persist (e.g. password fields). */
  exclude?: string[];
  /** Turn persistence on/off (default true). */
  enabled?: boolean;
  /** Debounce window for saves (default 600ms). */
  debounceMs?: number;
  /** Schema version — bump to invalidate incompatible older drafts. */
  version?: number;
  /** Drafts older than this are discarded (default 7 days). */
  maxAgeMs?: number;
  /** Optional side-effect after a draft is restored (e.g. sync external state). */
  onRestore?: (data: Record<string, unknown>) => void;
}

export function useFormDraft<T extends FieldValues>(
  form: UseFormReturn<T>,
  options: UseFormDraftOptions,
) {
  const { user } = useAuth();
  const {
    key,
    exclude = [],
    enabled = true,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    version = 1,
    maxAgeMs = DEFAULT_MAX_AGE_MS,
    onRestore,
  } = options;

  const scopedKey = scopedDraftKey(user?.id, key);

  // Refs so the watch subscription doesn't need to re-subscribe on every render.
  const restoredRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const excludeRef = useRef(exclude);
  excludeRef.current = exclude;
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  // Restore once on mount (per scoped key).
  useEffect(() => {
    if (!enabled) {
      restoredRef.current = true;
      return;
    }
    let cancelled = false;
    restoredRef.current = false;
    (async () => {
      const data = await loadDraft<Partial<T>>(scopedKey, { version, maxAgeMs });
      if (cancelled) return;
      if (data && typeof data === 'object') {
        // Merge over current values so any field missing from the draft keeps
        // its default. The restore itself must not trigger a re-save, so we
        // flip restoredRef true *after* reset.
        const merged = { ...form.getValues(), ...data } as T;
        form.reset(merged, { keepDefaultValues: true });
        onRestoreRef.current?.(data as Record<string, unknown>);
      }
      restoredRef.current = true;
    })();
    return () => {
      cancelled = true;
    };
    // form is stable from react-hook-form; intentionally keyed on scopedKey/enabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedKey, enabled]);

  // Debounced auto-save on every change.
  useEffect(() => {
    if (!enabled) return;
    const subscription = form.watch((values) => {
      // Don't persist until the initial restore attempt has run, so we never
      // clobber a saved draft with empty defaults during first render.
      if (!restoredRef.current) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const toSave: Record<string, unknown> = { ...(values as Record<string, unknown>) };
        for (const field of excludeRef.current) delete toSave[field];
        void saveDraft(scopedKey, toSave, version);
      }, debounceMs);
    });
    return () => {
      subscription.unsubscribe();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedKey, enabled, debounceMs, version]);

  return {
    /** Remove the saved draft — call after a successful submit or discard. */
    clearDraft: () => clearDraft(scopedKey),
  };
}
